import { RsuStatusCode } from '_store/devices/devicesTypes';
import { SpatemActivityStatus } from '_store/spatem/spatemsTypes';
import L, { Marker as LeafletMarker, MarkerCluster, Point } from 'leaflet';

import EmptyDenm from '../../../icons/denm/RedTriangle.png';
import EmptyIvim from '../../../icons/ivim/BlueExclamation.png';
import { iconSizePx, pmvStatusIcon, rsuStatusIcon, spatemIcon } from '../utils';
import { decodeClusterMeta } from './meta';
import {
  CLUSTER_GROUP_ORDER,
  CLUSTER_STATUS_ORDER,
  ClusterChip,
  ClusterGroup,
  ClusterStatus,
  EVENT_STATUS_COLORS,
} from './types';

const CHIP_SIZE = 28;
const CHIP_GAP = 4;
const CHIP_PAD = 4;

function chipIconUrl(group: ClusterGroup, status: ClusterStatus): string {
  switch (group) {
    case 'rsu':
      return rsuStatusIcon(status as RsuStatusCode);
    case 'pmv':
      return pmvStatusIcon(status === 'working');
    case 'spatem':
      return spatemIcon(
        status === 'active' ? SpatemActivityStatus.ACTIVE : SpatemActivityStatus.INACTIVE,
      );
    case 'denm':
      return EmptyDenm;
    case 'ivim':
      return EmptyIvim;
    default:
      return EmptyDenm;
  }
}

function isEventGroup(group: ClusterGroup): boolean {
  return group === 'denm' || group === 'ivim';
}

function aggregateChips(markers: LeafletMarker[]): ClusterChip[] {
  const counts = new Map<string, number>();

  markers.forEach((marker) => {
    const meta = decodeClusterMeta(marker.getAttribution());
    if (!meta) {
      return;
    }
    const key = `${meta.group}:${meta.status}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  const chips: ClusterChip[] = [];

  CLUSTER_GROUP_ORDER.forEach((group) => {
    CLUSTER_STATUS_ORDER[group].forEach((status) => {
      const count = counts.get(`${group}:${status}`);
      if (!count) {
        return;
      }
      chips.push({
        group,
        status: status as ClusterStatus,
        count,
        iconUrl: chipIconUrl(group, status as ClusterStatus),
        fullContainer: isEventGroup(group),
        accentColor: isEventGroup(group) ? EVENT_STATUS_COLORS[status] : undefined,
      });
    });
  });

  return chips;
}

function countBadge(count: number, className = 'cluster-count'): string {
  const digits = count.toString().length;
  return `<div class="${className}" data-digits="${digits}">${count}</div>`;
}

function monoChipHtml(chip: ClusterChip): string {
  if (chip.fullContainer) {
    const accent = chip.accentColor ? ` style="border-color:${chip.accentColor}"` : '';
    return `
      <div class="cluster-icon-container-full"${accent}>
        <img src="${chip.iconUrl}" class="cluster-icon" alt="" />
        ${countBadge(chip.count)}
      </div>
    `;
  }

  return `
    <div class="cluster-icon-container">
      <img src="${chip.iconUrl}" class="cluster-icon" alt="" />
      ${countBadge(chip.count)}
    </div>
  `;
}

function multiChipHtml(chips: ClusterChip[]): string {
  const chipsHtml = chips
    .map((chip) => {
      const accent = chip.accentColor
        ? ` style="box-shadow: inset 0 0 0 2px ${chip.accentColor}"`
        : '';
      return `
        <div class="cluster-chip"${accent}>
          <img src="${chip.iconUrl}" class="cluster-chip-icon" alt="" />
          ${countBadge(chip.count, 'cluster-chip-count')}
        </div>
      `;
    })
    .join('');

  return `<div class="cluster-composite">${chipsHtml}</div>`;
}

export function createCompositeClusterIcon(cluster: MarkerCluster): L.DivIcon {
  const markers = cluster.getAllChildMarkers() as LeafletMarker[];
  const chips = aggregateChips(markers);

  if (chips.length === 0) {
    const count = markers.length;
    return L.divIcon({
      html: `<div class="cluster-icon-container"><div class="cluster-count" data-digits="${
        count.toString().length
      }">${count}</div></div>`,
      className: 'marker-event-unselected',
      iconSize: new Point(iconSizePx, iconSizePx),
      iconAnchor: new Point(iconSizePx / 2, iconSizePx),
    });
  }

  if (chips.length === 1) {
    const chip = chips[0];
    const size = chip.fullContainer ? 40 : iconSizePx;
    return L.divIcon({
      html: monoChipHtml(chip),
      className: 'marker-event-unselected',
      iconSize: new Point(size, size),
      iconAnchor: new Point(size / 2, size),
    });
  }

  const width = chips.length * CHIP_SIZE + (chips.length - 1) * CHIP_GAP + CHIP_PAD * 2;
  const height = CHIP_SIZE + CHIP_PAD * 2 + 10;

  return L.divIcon({
    html: multiChipHtml(chips),
    className: 'marker-event-unselected cluster-composite-wrapper',
    iconSize: new Point(width, height),
    iconAnchor: new Point(width / 2, height),
  });
}
